import { IDashboardBuildingPayload } from "../../../services/zeos-api/contracts/Dashboard";

export const SAVINGS_SIGN_NOTE =
  "A negative value means the building used more energy than in the baseline year.";

/** Generic on purpose: why it failed is in savingsDetail and the API log, for developers only. */
export const SAVINGS_UNAVAILABLE_NOTE = "Savings can't be shown right now.";

const signed = (value: number) => {
  const formatted = Math.abs(value).toLocaleString("en-US", { maximumFractionDigits: 0 });
  if (value > 0) {
    return `+${formatted}`;
  }
  return value < 0 ? `-${formatted}` : formatted;
};

const hasFigure = (data: IDashboardBuildingPayload): boolean =>
  data.savingsStatus !== "unavailable" && data.savingsGenerated != null;

const someMonthsIncomplete = (data: IDashboardBuildingPayload) =>
  data.savingsMonthsUsed != null &&
  data.savingsMonthsInRange != null &&
  data.savingsMonthsUsed < data.savingsMonthsInRange;

/**
 * At most two short lines under the Building tile's Savings Generated figure (bug linked to 4053): the kWh /
 * therms change vs the baseline year, then "Delivery only · EV excluded · N months billed". The tile is a fixed
 * 220px, so the full sentences live in buildingSavingsHint (hover). With no figure: the API's savingsMessage, or
 * always the generic line when savings are "unavailable" (savingsDetail is never shown). Never builds a figure
 * the API didn't send.
 */
export const buildingSavingsNote = (data?: IDashboardBuildingPayload | null): string[] => {
  if (!data) {
    return [];
  }
  if (data.savingsStatus === "unavailable") {
    return [SAVINGS_UNAVAILABLE_NOTE];
  }
  if (!hasFigure(data)) {
    return data.savingsMessage ? [data.savingsMessage] : [];
  }

  const usage: string[] = [];
  if (data.savingsKwh != null) {
    usage.push(`${signed(data.savingsKwh)} kWh`);
  }
  if (data.savingsTherms != null) {
    usage.push(`${signed(data.savingsTherms)} therms`);
  }
  const vs = data.baselineLabel ? `vs ${data.baselineLabel}` : "vs baseline year";
  const lines = [usage.length > 0 ? `${usage.join(" · ")} ${vs}` : `Change ${vs}`];

  const tags: string[] = [];
  if (data.savingsBasis === "delivery_only") {
    tags.push("Delivery only");
  }
  if (data.savingsEvExcluded) {
    tags.push(data.savingsEvEstimated ? "EV excluded (partly estimated)" : "EV excluded");
  }
  if (someMonthsIncomplete(data)) {
    tags.push(`${data.savingsMonthsUsed} months billed`);
  }
  if (tags.length > 0) {
    lines.push(tags.join(" · "));
  }
  return lines;
};

/** The full explanation behind the two lines, shown on hover of Savings Generated. Undefined when there's no figure. */
export const buildingSavingsHint = (data?: IDashboardBuildingPayload | null): string | undefined => {
  if (!data || !hasFigure(data)) {
    return undefined;
  }

  const sentences = [
    data.baselineLabel
      ? `Savings Generated compares the building's energy use with the baseline year (${data.baselineLabel}).`
      : "Savings Generated compares the building's energy use with the baseline year.",
    SAVINGS_SIGN_NOTE,
  ];
  if (data.savingsBasis === "delivery_only") {
    sentences.push("Delivery charges only; supply is billed separately.");
  }
  if (data.savingsEvExcluded) {
    sentences.push(
      data.savingsEvEstimated ? "EV charging is excluded (partly estimated)." : "EV charging is excluded."
    );
  }
  if (someMonthsIncomplete(data)) {
    sentences.push(
      `Based on ${data.savingsMonthsUsed} of ${data.savingsMonthsInRange} months with complete bills.`
    );
  }
  return sentences.join(" ");
};
